import moment from 'moment'
import { Model } from 'libx'
import { observable, computed } from 'mobx'
import identity from 'lodash/identity'
import Amendment from './Amendment'
import { rulesEngine } from '@taxfyle/rules-engine'
import { makeMomentSorter } from 'utils/arrayUtil'
import { omitUndefined } from '../utils/objectUtil'

export default class Project extends Model {
  @observable name
  @observable answers = []
  @observable amendments = []
  @observable unseenMessagesCount = 0
  @observable conversation
  @observable provider = null
  @observable partnerRating = null
  @observable dateCompleted = null
  @observable dateCreated = null
  @observable dateUpdated = null
  @observable dateReopened = null
  @observable dateTransmitted = null
  @observable conversationId = null
  @observable.ref legend = null
  @observable status = 'UNDER_CONSTRUCTION'
  @observable states = []
  @observable years = []
  @observable total = 0
  @observable paidAmount = 0
  @observable primaryAmount = 0
  @observable adminFeeAmount = 0
  @observable federalAmount = null // this was not always present.
  @observable stateAmount = null // this was not always present.
  @observable couponAmount = 0
  @observable amendedAmount = 0

  @computed get completed () {
    return this.status === 'CLOSED'
  }

  @computed get projectType () {
    return this.legend ? this.legend.id : null
  }

  @computed get documents () {
    // const category = this.documentsCategory
    return this.rootStore.documentStore.allDocuments
  }

  @computed get unreadCount () {
    return this.rootStore.messageStore.unreadIn(this.conversationId)
  }

  @computed get events () {
    return this.rootStore.eventStore.jobEvents.filter(x => x.jobId === this.id).sort(makeMomentSorter('desc', x => x.dateCreated))
  }

  @computed get documentsCategory () {
    return this.projectType === 'PERSONAL_TAX_RETURN'
      ? 'PERSONAL'
      : 'BUSINESS'
  }

  @computed get questions () {
    if (!this.legend) {
      return []
    }

    return rulesEngine(this.legend).questions(this.answers || [])
  }

  @computed get hasAnsweredAllQuestions () {
    if (!this.legend) {
      return false
    }

    const questions = rulesEngine(this.legend).questions(this.answers || [])
    return questions.filter(x => x.answer === undefined).length === 0
  }

  @computed get timeline () {
    return [
      {
        date: this.dateCreated,
        text: 'Job created',
        type: 'pending'
      },
      this.dateTransmitted && {
        date: moment(this.dateTransmitted),
        text: `Submitted`,
        type: 'pending'
      },
      this.dateAccepted && {
        date: moment(this.dateAccepted),
        text: `Job picked up by ${this.provider.name}`,
        type: 'pending'
      },
      !this.dateCompleted && this.dateReopened && {
        date: moment(this.dateReopened),
        text: 'Job reopened',
        type: 'pending'
      },
      this.dateCompleted && {
        date: this.dateCompleted,
        text: 'Job completed',
        type: 'success'
      }
    ].filter(identity)
  }

  @computed get statusMessage () {
    switch (this.status) {
      case 'UNDER_CONSTRUCTION':
        return 'Under Construction'
      case 'UNCLAIMED':
        return 'Submitted'
      case 'CLAIMED':
        return 'In Progress'
      case 'CLOSED':
        return 'Completed'
      default:
        return 'N/A'
    }
  }

  @computed get relevantStatusDate () {
    switch (this.status) {
      case 'UNDER_CONSTRUCTION':
        return this.dateCreated
      case 'UNCLAIMED':
        return this.dateTransmitted
      case 'CLAIMED':
        return this.dateAccepted
      case 'CLOSED':
        return this.dateCompleted
      default:
        return this.dateUpdated
    }
  }

  parse (json) {
    const provider = json.provider_user_id
      ? (
        this.rootStore.userStore.users.set({
          user_id: json.provider_user_id,
          ...!this.rootStore.userStore.users.get(json.provider_user_id)
            ? { given_name: json.provider_first, family_name: json.provider_last }
            : null
        })
      )
      : null

    const conversation = json.layerConversation
      ? this.rootStore.messageStore.conversations.set({ id: json.layerConversation })
      : null

    return omitUndefined({
      id: json.id,
      name: json.description,
      providerId: json.provider_user_id,
      conversation,
      amendments: json.amendments
        ? json.amendments.map(x => new Amendment(x, { parse: true, rootStore: this.rootStore, job: this }))
        : undefined,
      legend: json.legend,
      answers: json.answers,
      states: json.coverage_states,
      status: json.status,
      years: json.years,
      total: json.total,
      adminFeeAmount: json.admin_fee_amount,
      paidAmount: json.paid_amount,
      primaryAmount: json.primary_amount,
      stateAmount: json.state_amount,
      federalAmount: json.federal_amount,
      couponAmount: json.coupon_amount,
      amendedAmount: json.amended_amount,
      // If we didn't get provider data (missing), just use the current provider.
      // If it was explicitly null, it means the provider has been removed, and so we null
      // the provider on the job.
      provider: provider || (json.provider_user_id === null ? null : this.provider),
      partnerRating: json.rating_submitted,
      customerRating: json.rating_received,
      conversationId: json.layerConversation,
      dateCreated: moment(json.date_created),
      dateUpdated: moment(json.date_modified),
      // Note: the reason for the logic below is that if the value
      // is falsy, it is most likely explicitly being set to null (which may be desired)
      dateCompleted: json.date_closed ? moment(json.date_closed) : json.date_closed,
      dateAccepted: json.date_accepted ? moment(json.date_accepted) : json.date_accepted,
      dateTransmitted: json.date_transmitted ? moment(json.date_transmitted) : json.date_transmitted,
      dateReopened: json.date_reopened ? moment(json.date_reopened) : json.date_reopened
    })
  }
}



// WEBPACK FOOTER //
// ./src/client/domain/Project.js